import { addToast, Modal } from '@platform/components';
import { useQueryClient } from '@tanstack/react-query';
import { MyProjects } from '@platform/client';
import { useContext, createContext, useState } from 'react';

interface DeleteProjectModalContext {
  isOpen: boolean;
  openDeleteProjectModal: (projectId: number) => void;
  closeDeleteProjectModal: () => void;
  deleteProjectId?: number;
}

export const DeleteProjectModalContext = createContext<DeleteProjectModalContext>({
  isOpen: false,
  openDeleteProjectModal: () => {},
  closeDeleteProjectModal: () => {},
});

export const DeleteProjectModalProvider = ({ children }: { children: React.ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [deleteProjectId, setDeleteProjectId] = useState<number | undefined>(undefined);
  const openDeleteProjectModal = (projectId: number) => {
    setDeleteProjectId(projectId);
    setIsOpen(true);
  };
  const closeDeleteProjectModal = () => {
    setDeleteProjectId(undefined);
    setIsOpen(false);
  };
  return (
    <DeleteProjectModalContext.Provider
      value={{
        isOpen,
        deleteProjectId,
        openDeleteProjectModal,
        closeDeleteProjectModal,
      }}
    >
      {children}
      {isOpen && <DeleteProjectModal />}
    </DeleteProjectModalContext.Provider>
  );
};

export const DeleteProjectModal = () => {
  const { isOpen, deleteProjectId, closeDeleteProjectModal } =
    useContext(DeleteProjectModalContext);

  const queryClient = useQueryClient();
  const deleteProjectMutation = MyProjects.useDeleteProject({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: MyProjects.getGetOrgUserMyProjectsQueryKey() });
        closeDeleteProjectModal();
        addToast({
          description: 'Project successfully deleted.',
        });
      },
    },
  });

  if (!deleteProjectId) {
    return null;
  }

  return (
    <Modal
      variant='dialog'
      headerDivision='divider'
      title='Delete project'
      isOpen={isOpen}
      onClose={closeDeleteProjectModal}
      primaryCTA={{
        text: 'Delete project',
        callback: () => {
          deleteProjectMutation.mutate({
            params: {
              project_id: deleteProjectId,
            },
          });
        },
        buttonVariant: 'primary-danger',
      }}
      secondaryCTA={{
        text: 'Cancel',
        callback: () => {
          closeDeleteProjectModal();
        },
      }}
      ignoreExtraPadding
    >
      <div className='w-[544px] flex flex-col gap-4'>
        <div>Are you sure you want to delete this project?</div>
        <div>This action cannot be undone.</div>
      </div>
    </Modal>
  );
};
